import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText,
  Clock,
  Package,
  CheckCircle,
  Truck,
  Wrench,
  HouseLine,
  CalendarCheck,
  Warning,
  IdentificationBadge,
  FileXls,
  X
} from '@phosphor-icons/react';
import { SearchFilterBar, EmptySearchResult, LoadErrorState } from '../components/SearchFilterBar';
import { JOSidePanel, DrillDownJobList } from '../components/JOSidePanel';

const STATUS_LABELS = {
  pending_procurement: 'Pending Procurement',
  procurement_approved: 'Approved',
  procurement_rejected: 'Rejected',
  assigned_to_production: 'Assigned to Production',
  under_production: 'Under Production',
  production_completed: 'Production Completed',
  scheduled_for_dispatch: 'Scheduled for Dispatch',
  partially_dispatched: 'Partially Dispatched',
  dispatched: 'Dispatched',
  pending_for_installation: 'Pending Installation',
  installation_completed: 'Installation Completed'
};

const STATUS_COLORS = {
  pending_procurement: 'bg-amber-100 text-amber-700',
  procurement_approved: 'bg-blue-100 text-blue-700',
  procurement_rejected: 'bg-red-100 text-red-700',
  assigned_to_production: 'bg-purple-100 text-purple-700',
  under_production: 'bg-violet-100 text-violet-700',
  production_completed: 'bg-cyan-100 text-cyan-700',
  scheduled_for_dispatch: 'bg-yellow-100 text-yellow-700',
  partially_dispatched: 'bg-orange-100 text-orange-700',
  dispatched: 'bg-emerald-100 text-emerald-700',
  pending_for_installation: 'bg-indigo-100 text-indigo-700',
  installation_completed: 'bg-teal-100 text-teal-700'
};

function StatCard({ label, value, icon: Icon, color, testId, onClick, active }) {
  return (
    <Card
      data-testid={testId}
      className={`border-zinc-300 rounded-sm shadow-sm transition-all ${
        onClick ? 'cursor-pointer hover:border-orange-400 hover:shadow-md' : ''
      } ${active ? 'border-orange-500 ring-2 ring-orange-200 bg-orange-50/30' : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
            <p className="text-2xl font-light text-zinc-950 mt-1">{value}</p>
          </div>
          <Icon size={24} weight="bold" className={color} />
        </div>
      </CardContent>
    </Card>
  );
}

/* ========== Executive-wise Report for Sales ========== */
function SalesExecutiveReport() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [execFilter, setExecFilter] = useState('all');
  const [execNames, setExecNames] = useState([]);
  const [expandedExec, setExpandedExec] = useState(null);

  useEffect(() => {
    api.get(`/api/reports/executive-names`)
      .then(res => setExecNames(res.data))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async (overrides) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const df = overrides?.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
      const dt = overrides?.dateTo !== undefined ? overrides.dateTo : dateTo;
      const ef = overrides?.execFilter !== undefined ? overrides.execFilter : execFilter;
      if (df) params.append('date_from', df);
      if (dt) params.append('date_to', dt);
      if (ef && ef !== 'all') params.append('executive_name', ef);
      const res = await api.get(`/api/reports/executive-wise?${params}`);
      setData(res.data);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load executive report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, execFilter]);

  useEffect(() => { fetchReport(); }, []);

  const exportCSV = () => {
    if (!data?.executives?.length) return;
    const rows = [];
    for (const exec of data.executives) {
      for (const jo of exec.jobs) {
        rows.push([exec.executive_name, jo.jo_number, jo.customer_name, jo.customer_mobile, jo.work_location, jo.delivery_date, jo.no_of_windows, jo.total_sqft, jo.status].join(','));
      }
    }
    const csv = ['Executive,JO No,Customer,Mobile,Location,Delivery,Windows,Sqft,Status', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `executive_wise_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Executive Name</Label>
          <Select value={execFilter} onValueChange={setExecFilter}>
            <SelectTrigger data-testid="sales-exec-filter" className="w-56 h-10">
              <SelectValue placeholder="All Executives" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Executives</SelectItem>
              {execNames.map(name => (
                <SelectItem key={name} value={name}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">From Date</Label>
          <Input data-testid="sales-exec-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-44" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">To Date</Label>
          <Input data-testid="sales-exec-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-44" />
        </div>
        <Button data-testid="sales-exec-export" onClick={exportCSV} disabled={!data?.executives?.length} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10">
          <FileXls size={16} weight="bold" className="mr-2" /> Export CSV
        </Button>
      </div>

      {loading && <div className="text-center py-8 text-zinc-500">Loading...</div>}

      {!loading && data && (
        <>
          <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-around flex-wrap gap-4">
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Executives</p>
                  <p className="text-2xl font-light text-zinc-950">{data.executives.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Jobs</p>
                  <p className="text-2xl font-light text-zinc-950">{data.grand_totals.total_jobs}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                  <p className="text-2xl font-light text-zinc-950">{data.grand_totals.total_windows}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
                  <p className="text-2xl font-light text-zinc-950">{data.grand_totals.total_sqft}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-300 rounded-sm shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Executive</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Jobs</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Pending</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">In Prod</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Completed</th>
                      <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.executives.map(exec => (
                      <tr key={exec.executive_name} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="p-3 font-bold text-zinc-950">{exec.executive_name}</td>
                        <td className="p-3 text-center text-zinc-700">{exec.total_jobs}</td>
                        <td className="p-3 text-center text-zinc-700">{exec.total_windows}</td>
                        <td className="p-3 text-center text-zinc-700">{exec.total_sqft}</td>
                        <td className="p-3 text-center"><span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-bold">{exec.pending}</span></td>
                        <td className="p-3 text-center"><span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-bold">{exec.in_production}</span></td>
                        <td className="p-3 text-center"><span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-bold">{exec.completed}</span></td>
                        <td className="p-3 text-center">
                          <button data-testid={`sales-expand-${exec.executive_name}`} onClick={() => setExpandedExec(expandedExec === exec.executive_name ? null : exec.executive_name)} className="text-orange-600 hover:text-orange-700 text-xs font-bold underline">
                            {expandedExec === exec.executive_name ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {expandedExec && data.executives.find(e => e.executive_name === expandedExec) && (
            <Card className="border-orange-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-orange-200 bg-orange-50 py-3">
                <CardTitle className="text-lg font-bold text-zinc-950">Job Orders by {expandedExec}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Mobile</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Location</th>
                        <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Delivery</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                        <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.executives.find(e => e.executive_name === expandedExec).jobs.map(jo => (
                        <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                          <td className="p-3 font-bold text-zinc-950">{jo.jo_number}</td>
                          <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                          <td className="p-3 text-zinc-700">{jo.customer_mobile}</td>
                          <td className="p-3 text-zinc-700">{jo.work_location}</td>
                          <td className="p-3 text-zinc-700">{jo.delivery_date}</td>
                          <td className="p-3 text-center text-zinc-700">{jo.no_of_windows}</td>
                          <td className="p-3 text-center text-zinc-700">{jo.total_sqft}</td>
                          <td className="p-3 text-center text-xs font-bold text-zinc-600">{jo.status?.replace(/_/g, ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

const SALES_SEARCH_FIELDS = [
  { key: 'search', label: 'Search', placeholder: 'JO number, customer, or mobile...' },
  { key: 'dateFrom', label: 'From Date', type: 'date' },
  { key: 'dateTo', label: 'To Date', type: 'date' },
  {
    key: 'statusFilter', label: 'Status', type: 'select',
    options: [
      { value: 'all', label: 'All Statuses' },
      ...Object.entries(STATUS_LABELS).map(([key, label]) => ({ value: key, label }))
    ]
  }
];

const SALES_DEFAULT_FILTERS = { search: '', dateFrom: '', dateTo: '', statusFilter: 'all' };
const PAGE_SIZE = 50;

export function SalesDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState(SALES_DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(SALES_DEFAULT_FILTERS);
  const [drillDown, setDrillDown] = useState(null);
  const [drillDownJobs, setDrillDownJobs] = useState([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [sidePanelJO, setSidePanelJO] = useState(null);
  const drillDownRef = useRef(null);
  const navigate = useNavigate();

  const handleSalesCardClick = useCallback(async (label, sf) => {
    if (drillDown?.sf === sf) {
      setDrillDown(null);
      setDrillDownJobs([]);
      setSidePanelJO(null);
      return;
    }
    setDrillDown({ label, sf });
    setSidePanelJO(null);
    setDrillDownLoading(true);
    try {
      const statuses = Array.isArray(sf) ? sf : [sf];
      const results = await Promise.all(
        statuses.map(s => api.get(`/api/job-orders?status=${s}`))
      );
      setDrillDownJobs(results.flatMap(r => r.data || []));
      setTimeout(() => drillDownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch {
      setDrillDownJobs([]);
    } finally {
      setDrillDownLoading(false);
    }
  }, [drillDown]);


  const fetchData = useCallback(async () => {
    setLoadError(false);
    try {
      const statsRes = await api.get(`/api/sales/dashboard-stats`);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [jobOrders, setJobOrders] = useState([]);
  const [joListLoading, setJoListLoading] = useState(false);
  const [joLoadError, setJoLoadError] = useState(false);
  const [joTotal, setJoTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-indexed

  // Reset to first page whenever applied filters change
  useEffect(() => { setPage(0); }, [appliedFilters]);

  const fetchJobOrders = useCallback(async () => {
    setJoListLoading(true);
    setJoLoadError(false);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.search) {
        params.append('search', appliedFilters.search);
      }
      if (appliedFilters.statusFilter && appliedFilters.statusFilter !== 'all') {
        params.append('status', appliedFilters.statusFilter);
      }
      if (appliedFilters.dateFrom) params.append('jo_date_from', appliedFilters.dateFrom);
      if (appliedFilters.dateTo) params.append('jo_date_to', appliedFilters.dateTo);
      params.append('slim', 'true');
      params.append('limit', String(PAGE_SIZE));
      params.append('skip', String(page * PAGE_SIZE));
      const res = await api.get(`/api/job-orders?${params}`);
      setJobOrders(res.data || []);
      const totalHeader = res.headers?.['x-total-count'] ?? res.headers?.['X-Total-Count'];
      setJoTotal(totalHeader ? parseInt(totalHeader, 10) : (res.data?.length || 0));
    } catch (err) {
      if (err?.isAuthError) return;
      setJoLoadError(true);
      setJobOrders([]);
      setJoTotal(0);
      toast.error('Failed to load job orders');
    }
    finally { setJoListLoading(false); }
  }, [appliedFilters, page]);

  // Load JO list when on the Dashboard tab or when filters/page change
  useEffect(() => {
    if (activeTab === 'dashboard') fetchJobOrders();
  }, [activeTab, fetchJobOrders]);

  // Also trigger fetch when search/reset is clicked
  const handleSearch = (merged) => { setAppliedFilters(merged || filters); };
  const handleReset = (cleared) => { setFilters(cleared); setAppliedFilters(cleared); };

  if (loading) {
    return <Layout><div className="text-center py-12">Loading dashboard...</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">Sales Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-2">View-only access to Job Order statuses</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-300">
          <button
            data-testid="sales-tab-dashboard"
            onClick={() => setActiveTab('dashboard')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'dashboard'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <FileText size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Dashboard
          </button>
          <button
            data-testid="sales-tab-executive"
            onClick={() => setActiveTab('executive-wise')}
            className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
              activeTab === 'executive-wise'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <IdentificationBadge size={16} weight="bold" className="inline mr-2 mb-0.5" />
            Executive-wise
          </button>
        </div>

        {activeTab === 'executive-wise' && <SalesExecutiveReport />}



        {activeTab === 'dashboard' && (
          <>
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <StatCard label="Total Jobs" value={stats.total_jobs} icon={FileText} color="text-blue-600" testId="sales-total" />
                <StatCard label="Pending" value={stats.pending_procurement} icon={Clock} color="text-amber-600" testId="sales-pending"
                  onClick={() => handleSalesCardClick('Pending', 'pending_procurement')}
                  active={drillDown?.sf === 'pending_procurement'}
                />
                <StatCard label="Approved" value={stats.procurement_approved} icon={CheckCircle} color="text-blue-600" testId="sales-approved"
                  onClick={() => handleSalesCardClick('Approved', 'procurement_approved')}
                  active={drillDown?.sf === 'procurement_approved'}
                />
                <StatCard label="In Production" value={(stats.assigned_to_production || 0) + (stats.under_production || 0)} icon={Package} color="text-purple-600" testId="sales-production"
                  onClick={() => handleSalesCardClick('In Production', 'in_production')}
                  active={drillDown?.sf === 'in_production'}
                />
                <StatCard label="Ready to Dispatch" value={stats.production_completed} icon={CheckCircle} color="text-cyan-600" testId="sales-prod-done"
                  onClick={() => handleSalesCardClick('Ready to Dispatch', 'production_completed')}
                  active={drillDown?.sf === 'production_completed'}
                />
                <StatCard label="Scheduled" value={stats.scheduled_for_dispatch} icon={CalendarCheck} color="text-yellow-600" testId="sales-scheduled"
                  onClick={() => handleSalesCardClick('Scheduled', 'scheduled_for_dispatch')}
                  active={drillDown?.sf === 'scheduled_for_dispatch'}
                />
                <StatCard label="Dispatched" value={(stats.dispatched || 0) + (stats.partially_dispatched || 0)} icon={Truck} color="text-emerald-600" testId="sales-dispatched"
                  onClick={() => handleSalesCardClick('Dispatched', 'dispatched')}
                  active={drillDown?.sf === 'dispatched'}
                />
                <StatCard label="Pending Install" value={stats.pending_for_installation} icon={Wrench} color="text-indigo-600" testId="sales-pending-install"
                  onClick={() => handleSalesCardClick('Pending Install', 'pending_for_installation')}
                  active={drillDown?.sf === 'pending_for_installation'}
                />
                <StatCard label="Install Done" value={stats.installation_completed} icon={HouseLine} color="text-teal-600" testId="sales-install-done"
                  onClick={() => handleSalesCardClick('Install Done', 'installation_completed')}
                  active={drillDown?.sf === 'installation_completed'}
                />
              </div>
            )}

            {/* Drill-Down from Card Click */}
            {drillDown && (
              <Card ref={drillDownRef} data-testid="sales-drill-down" className="border-orange-300 rounded-sm shadow-sm">
                <CardHeader className="border-b border-orange-200 bg-orange-50/50 py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-zinc-950">
                      {drillDown.label} ({drillDownLoading ? '...' : drillDownJobs.length})
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => { setDrillDown(null); setDrillDownJobs([]); }} className="h-8 w-8 p-0">
                      <X size={18} weight="bold" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {drillDownLoading ? (
                    <div className="p-6 text-center text-zinc-400">Loading...</div>
                  ) : (
                    <DrillDownJobList
                      jobs={drillDownJobs}
                      title={drillDown.label}
                      onSelectJO={setSidePanelJO}
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Search & Filters */}
            <SearchFilterBar
              fields={SALES_SEARCH_FIELDS}
              filters={filters}
              setFilters={setFilters}
              onSearch={handleSearch}
              onReset={handleReset}
              onRefresh={() => { fetchData(); if (activeTab === 'dashboard') fetchJobOrders(); }}
              loading={loading || joListLoading}
              resultCount={joTotal}
            />

            {/* Job Orders Table */}
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">
                  Job Orders ({joListLoading ? '...' : joTotal})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {joListLoading ? (
                  <div className="p-8 text-center text-zinc-400">Loading job orders...</div>
                ) : joLoadError ? (
                  <LoadErrorState onRetry={fetchJobOrders} />
                ) : jobOrders.length === 0 ? (
                  <EmptySearchResult onReset={() => handleReset(SALES_DEFAULT_FILTERS)} />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-200 bg-zinc-50">
                          <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
                          <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
                          <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Mobile</th>
                          <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Executive</th>
                          <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Delivery</th>
                          <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Windows</th>
                          <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Sqft</th>
                          <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobOrders.map(jo => (
                          <tr key={jo.jo_number} className="border-b border-zinc-100 hover:bg-zinc-50">
                            <td className="p-3 font-bold text-zinc-950 cursor-pointer hover:text-orange-600" onClick={() => navigate(`/job-orders/${jo.jo_number}`)}>{jo.jo_number}</td>
                            <td className="p-3 text-zinc-700">{jo.customer_name}</td>
                            <td className="p-3 text-zinc-700">{jo.customer_mobile}</td>
                            <td className="p-3 text-zinc-700">{jo.executive_name}</td>
                            <td className="p-3 text-zinc-700">{jo.delivery_date}</td>
                            <td className="p-3 text-center text-zinc-700">{jo.no_of_windows}</td>
                            <td className="p-3 text-center text-zinc-700">{jo.total_sqft}</td>
                            <td className="p-3 text-center">
                              <Badge className={`${STATUS_COLORS[jo.status] || 'bg-zinc-100 text-zinc-600'} text-xs`}>
                                {STATUS_LABELS[jo.status] || jo.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!joListLoading && !joLoadError && joTotal > PAGE_SIZE && (
                  <div data-testid="sales-pagination" className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 bg-zinc-50 text-sm">
                    <span className="text-zinc-600">
                      Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, joTotal)} of {joTotal}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid="sales-pagination-prev"
                        variant="outline"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        className="h-8"
                      >
                        Previous
                      </Button>
                      <span className="text-xs font-bold text-zinc-500">
                        Page {page + 1} of {Math.max(1, Math.ceil(joTotal / PAGE_SIZE))}
                      </span>
                      <Button
                        data-testid="sales-pagination-next"
                        variant="outline"
                        size="sm"
                        disabled={(page + 1) * PAGE_SIZE >= joTotal}
                        onClick={() => setPage(p => p + 1)}
                        className="h-8"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <JOSidePanel
        open={!!sidePanelJO}
        onClose={() => setSidePanelJO(null)}
        jobOrder={sidePanelJO}
      />
    </Layout>
  );
}
